import * as React from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'

import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineBusIcon from 'LuxKit/Icons/line/LineBusIcon'
import LineCameraPlusIcon from 'LuxKit/Icons/line/LineCameraPlusIcon'
import LineCoffeeIcon from 'LuxKit/Icons/line/LineCoffeeIcon'
import LineDrinksTrayIcon from 'LuxKit/Icons/line/LineDrinksTrayIcon'
import LineFootballBallIcon from 'LuxKit/Icons/line/LineFootballBallIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineShoppingBagIcon from 'LuxKit/Icons/line/LineShoppingBagIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineTreesIcon from 'LuxKit/Icons/line/LineTreesIcon'
import LineUniversityIcon from 'LuxKit/Icons/line/LineUniversityIcon'
import { getTripListPlaceCategory, type TripListPlaceCategory } from './tripListPlaceCategories'
import type { TripListRow, TripListRowType } from './tripListTypes'

export type TripListIconCategory =
  | 'accommodation'
  | 'experience'
  | TripListPlaceCategory
  | 'transport-filter'

interface TripListIconCategoryInput {
  type: TripListRowType
  primaryType?: string
  placeTypes?: ReadonlyArray<string>
  placeCategory?: string
  hasRow: boolean
}

const TypeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(28)};
  height: ${rem(28)};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  flex-shrink: 0;

  &.type-flight {
    background: ${props => props.theme.palette.product.flights.seats.upfront.lightBackground};
    color: ${props => props.theme.palette.product.flights.seats.upfront.normalForeground};
  }

  &.type-accommodation,
  &.type-experience {
    background: ${props => props.theme.palette.highlight.secondary.lightBackground};
    color: ${props => props.theme.palette.highlight.secondary.normalForeground};
  }

  &.type-dining {
    background: ${props => props.theme.palette.messaging.warning.lightBackground};
    color: ${props => props.theme.palette.messaging.warning.normalForeground};
  }

  &.type-activity {
    background: ${props => props.theme.palette.messaging.success.lightBackground};
    color: ${props => props.theme.palette.messaging.success.normalForeground};
  }

  &.type-transport,
  &.type-cruise {
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
    color: ${props => props.theme.palette.highlight.primary.normalForeground};
  }
`

export function getTripListIconCategory(input: TripListIconCategoryInput): TripListIconCategory {
  switch (input.type) {
    case 'accommodation':
      return 'accommodation'
    case 'experience':
      return 'experience'
    case 'dining': {
      const category = getTripListPlaceCategory({
        primaryType: input.primaryType,
        types: input.placeTypes,
        primaryTypeDisplayName: input.placeCategory,
      })
      return category === 'attraction' ? 'restaurant' : category
    }
    case 'flight':
      return 'flight'
    case 'transport':
      return input.hasRow ? 'transport' : 'transport-filter'
    case 'cruise':
      return 'cruise'
    case 'activity':
      return getTripListPlaceCategory({
        primaryType: input.primaryType,
        types: input.placeTypes,
        primaryTypeDisplayName: input.placeCategory,
      })
    default:
      return 'attraction'
  }
}

function getTripListIconCategoryInput(type: TripListRowType, row: TripListRow | undefined): TripListIconCategoryInput {
  const placeDetail = row?.kind === 'item' ? row.item.itemPlaceDetail : undefined

  return {
    type,
    primaryType: placeDetail?.primaryType,
    placeTypes: placeDetail?.types,
    placeCategory: placeDetail?.primaryTypeDisplayName,
    hasRow: !!row,
  }
}

function getIcon(category: TripListIconCategory): React.ReactNode {
  switch (category) {
    case 'accommodation':
      return <LineBedIcon size="S" />
    case 'experience':
      return <LineTicketIcon size="S" />
    case 'restaurant':
      return <LineRestaurantIcon size="S" />
    case 'cafe':
      return <LineCoffeeIcon size="S" />
    case 'park':
      return <LineTreesIcon size="S" />
    case 'shopping':
      return <LineShoppingBagIcon size="S" />
    case 'museum':
      return <LineUniversityIcon size="S" />
    case 'bar':
      return <LineDrinksTrayIcon size="S" />
    case 'sport':
      return <LineFootballBallIcon size="S" />
    case 'flight':
      return <LinePlaneFlyIcon size="S" />
    case 'transport':
      return <LineBusIcon size="S" />
    case 'transport-filter':
      return <LineMapMarkerIcon size="S" />
    case 'lodging':
      return <LineBedIcon size="S" />
    case 'cruise':
      return <LineShipIcon size="S" />
    case 'attraction':
      return <LineCameraPlusIcon size="S" />
    default:
      return null
  }
}

interface Props {
  type: TripListRowType
  row?: TripListRow
  className?: string
}

function TripListTypeIcon({ type, row, className }: Props) {
  const iconCategory = getTripListIconCategory(getTripListIconCategoryInput(type, row))

  return (
    <TypeIcon className={clsx(`type-${type}`, className)}>
      {getIcon(iconCategory)}
    </TypeIcon>
  )
}

export default TripListTypeIcon
